/**
 * Host-defined cancellation policy: full refund up to N days before check-in, up to two partial
 * refund steps, then no refund. Mirrors the backend's CancellationPolicyV2 shape.
 */
export const CUSTOM_POLICY_CODE = 'CUSTOM';
export const MAX_PARTIAL_STEPS = 2;

export interface PartialRefundBand {
  fromDaysBefore: number;
  toDaysBefore: number;
  refundPercentage: number;
}

export interface CustomCancellationPolicy {
  version?: number;
  fullRefundDaysBefore: number;
  partialRefunds: PartialRefundBand[];
  noRefundDaysBefore: number;
}

/** What the host types into the editor (strings, so half-typed numbers don't jump around). */
export interface CustomPolicyDraft {
  fullDays: string;
  /** Refund % of each partial step, in order (0–2 steps). */
  percents: string[];
  /** Where the first of two steps ends; only used with two steps. */
  midDays: string;
  /** Cancelling closer than this many days to check-in gets no refund. */
  noRefundDays: string;
}

export const emptyDraft = (): CustomPolicyDraft => ({
  fullDays: '',
  percents: [],
  midDays: '',
  noRefundDays: '',
});

const toInt = (value: string): number | null => {
  if (value.trim() === '' || !/^\d+$/.test(value.trim())) return null;
  return Number(value.trim());
};

/** Returns the first problem with the draft, or null when it is a valid policy. */
export const validateDraft = (draft: CustomPolicyDraft): string | null => {
  const full = toInt(draft.fullDays);
  if (full === null) return 'Enter how many days before check-in guests get a full refund.';
  const steps = draft.percents.length;
  if (steps === 0) return null;
  const last = toInt(draft.noRefundDays);
  if (last === null) return 'Enter the day count after which there is no refund.';
  for (const p of draft.percents) {
    const n = toInt(p);
    if (n === null || n < 1 || n > 99) return 'Partial refunds must be between 1% and 99%.';
  }
  if (steps === 2) {
    const mid = toInt(draft.midDays);
    if (mid === null) return 'Enter where the first partial refund ends.';
    if (!(full > mid && mid > last)) {
      return 'Days must go down: full refund days > first step end > no-refund days.';
    }
    if (Number(draft.percents[0]) <= Number(draft.percents[1])) {
      return 'The earlier partial refund should be larger than the later one.';
    }
  } else if (!(full > last)) {
    return 'The no-refund day count must be lower than the full-refund day count.';
  }
  return null;
};

export const draftToPolicy = (draft: CustomPolicyDraft): CustomCancellationPolicy | null => {
  if (validateDraft(draft) !== null) return null;
  const full = Number(draft.fullDays);
  const steps = draft.percents.length;
  if (steps === 0) {
    // No partial steps: anything inside the full-refund window is refunded, anything later is not.
    return { version: 2, fullRefundDaysBefore: full, partialRefunds: [], noRefundDaysBefore: full };
  }
  const last = Number(draft.noRefundDays);
  const partialRefunds: PartialRefundBand[] =
    steps === 1
      ? [{ fromDaysBefore: full, toDaysBefore: last, refundPercentage: Number(draft.percents[0]) }]
      : [
          {
            fromDaysBefore: full,
            toDaysBefore: Number(draft.midDays),
            refundPercentage: Number(draft.percents[0]),
          },
          {
            fromDaysBefore: Number(draft.midDays),
            toDaysBefore: last,
            refundPercentage: Number(draft.percents[1]),
          },
        ];
  return { version: 2, fullRefundDaysBefore: full, partialRefunds, noRefundDaysBefore: last };
};

export const policyToDraft = (policy?: CustomCancellationPolicy | null): CustomPolicyDraft => {
  if (!policy) return emptyDraft();
  const bands = policy.partialRefunds || [];
  return {
    fullDays: String(policy.fullRefundDaysBefore ?? ''),
    percents: bands.map((b) => String(b.refundPercentage)),
    midDays: bands.length === 2 ? String(bands[0].toDaysBefore) : '',
    noRefundDays: bands.length > 0 ? String(policy.noRefundDaysBefore) : '',
  };
};

const days = (n: number): string => `${n} day${n === 1 ? '' : 's'}`;

/** Plain-language rules, one per line. */
export const describePolicy = (policy: CustomCancellationPolicy): string[] => {
  const lines = [`Full refund if cancelled ${days(policy.fullRefundDaysBefore)} or more before check-in.`];
  (policy.partialRefunds || []).forEach((b) => {
    lines.push(
      `${b.refundPercentage}% refund if cancelled between ${b.fromDaysBefore} and ${days(b.toDaysBefore)} before check-in.`,
    );
  });
  const noRefundFrom = (policy.partialRefunds || []).length > 0 ? policy.noRefundDaysBefore : policy.fullRefundDaysBefore;
  lines.push(`No refund if cancelled less than ${days(noRefundFrom)} before check-in.`);
  return lines;
};
